import { useEffect, useRef, useState } from "react";
import { http, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { PackageCheck, Bell, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 15000;

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [660, 880, 1174];
    const dur = 0.14;
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, now + i * dur);
      g.gain.setValueAtTime(0.0001, now + i * dur);
      g.gain.exponentialRampToValueAtTime(0.3, now + i * dur + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * dur + dur - 0.02);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + i * dur);
      osc.stop(now + i * dur + dur);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 800);
  } catch {}
}

/**
 * Bell for HQ users: notifies when a branch closes EOD reconciliation and
 * sends its valas stock to Pusat. HQ admin opens the dialog, counts physical
 * total per currency, and posts to /hq-arrivals/{id}/reconcile.
 */
export default function HQArrivalsBell({ onCount } = {}) {
  const { user } = useAuth();
  const { currentBranch, branches } = useBranch();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [physical, setPhysical] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [hqBalances, setHqBalances] = useState({});
  const knownIds = useRef(new Set());

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const hq = (branches || []).find((b) => /pusat/i.test(b.name || "") || /^(KP|PUSAT|MC\s*1)$/i.test(b.name || "") || /^(KP|PUSAT)$/i.test(b.code || ""));
  const isHqUser = isAdmin && (!currentBranch || !hq || currentBranch.id === hq.id);

  const fetchPending = async () => {
    try {
      const { data } = await http.get("/hq-arrivals?status=pending");
      const arr = Array.isArray(data) ? data : [];
      // Detect brand-new arrivals since last poll → chime
      const newOnes = arr.filter((a) => !knownIds.current.has(a.id));
      arr.forEach((a) => knownIds.current.add(a.id));
      if (newOnes.length > 0 && knownIds.current.size > newOnes.length) {
        playChime();
        newOnes.forEach((a) => toast.info(`Setoran EOD dari ${a.from_branch_name} — perlu rekonsiliasi`, { duration: 5000 }));
      }
      setPending(arr);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!isHqUser) return;
    fetchPending();
    const t = setInterval(fetchPending, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHqUser]);

  useEffect(() => { onCount?.(pending.length); }, [pending]);

  const openReconcile = async (a) => {
    setSelected(a); setPhysical({}); setNote("");
    // Fetch current HQ balances to pre-fill "system" figures
    try {
      const { data } = await http.get("/cash-balances");
      const map = {}; (data || []).forEach((b) => { map[b.code] = b.amount || 0; });
      setHqBalances(map);
    } catch {}
  };

  const submit = async () => {
    if (!selected) return;
    const codes = (selected.entries || []).map((e) => e.code);
    const filtered = {};
    for (const c of codes) {
      if (physical[c] !== undefined && physical[c] !== "") filtered[c] = parseFloat(physical[c]);
    }
    if (!Object.keys(filtered).length) return toast.error("Isi minimal 1 hitung fisik");
    setSaving(true);
    try {
      const { data } = await http.post(`/hq-arrivals/${selected.id}/reconcile`, { physical: filtered, note });
      const off = (data.verified || []).filter((v) => v.difference !== 0).length;
      toast.success(off === 0 ? "Rekonsiliasi selesai — semua stok sesuai" : `Rekonsiliasi selesai — ${off} mata uang dikoreksi`);
      setSelected(null); setPhysical({}); setNote("");
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal rekonsiliasi");
    } finally { setSaving(false); }
  };

  if (!isHqUser) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white/60 text-slate-600 hover:bg-slate-100 transition"
        data-testid="hq-arrivals-bell"
        title="Setoran EOD dari cabang"
      >
        <PackageCheck size={18} className={pending.length > 0 ? "text-emerald-600" : "text-slate-500"} />
        {pending.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center">
            {pending.length}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell size={16} /> Setoran EOD dari Cabang</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="hq-arrivals-list">
            {pending.length === 0 && <div className="text-sm text-slate-500 py-8 text-center">Tidak ada setoran menunggu rekonsiliasi.</div>}
            {pending.map((a) => (
              <div key={a.id} className="border rounded-md p-3 hover:border-emerald-400 transition">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-800">{a.from_branch_name}</div>
                    <div className="text-[11px] text-slate-500">
                      Closing {new Date(a.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} · oleh {a.actor_name}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => openReconcile(a)} data-testid={`btn-reconcile-${a.id}`}>
                    <ClipboardCheck size={13} className="mr-1.5" /> Rekonsiliasi
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(a.entries || []).map((e, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[11px] font-mono-num text-emerald-800">
                      {e.code} {fmtNum(e.amount, 2)}
                    </span>
                  ))}
                </div>
                {a.note && <div className="text-[11px] text-slate-500 mt-1.5 italic">"{a.note}"</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Rekonsiliasi Fisik — Setoran {selected?.from_branch_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-slate-500">
              Hitung fisik total stok valas di Pusat untuk setiap mata uang di bawah. Selisih akan dikoreksi otomatis di ledger Pusat.
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1.5">Mata Uang</th>
                  <th className="text-right px-2 py-1.5">Sistem</th>
                  <th className="text-right px-2 py-1.5">Fisik di Pusat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="hq-reconcile-table">
                {(selected?.entries || []).map((e, i) => {
                  const sys = hqBalances[e.code] || 0;
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1.5 font-semibold">
                        {e.code}
                        <div className="text-[10px] text-slate-500 font-mono-num">+{fmtNum(e.amount, 2)} baru masuk</div>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono-num">{fmtNum(sys, 2)}</td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.01" placeholder={fmtNum(sys, 2)}
                          value={physical[e.code] ?? ""}
                          onChange={(ev) => setPhysical((p) => ({ ...p, [e.code]: ev.target.value }))}
                          className="h-7 text-xs font-mono-num text-right"
                          data-testid={`hq-recon-input-${e.code}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Catatan (opsional)…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-submit-hq-recon">
              {saving ? "Menyimpan…" : "Simpan Rekonsiliasi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
